/**
 * Input Component
 * Reusable input component with label, error state, and variants.
 */

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.css';

/**
 * Input component props.
 */
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Input label */
  label?: string;
  /** Helper text below input */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Icon to display at start of input */
  startIcon?: ReactNode;
  /** Icon to display at end of input */
  endIcon?: ReactNode;
  /** Full width input */
  fullWidth?: boolean;
}

/**
 * Reusable Input component with label and error handling.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      startIcon,
      endIcon,
      fullWidth = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${label?.toLowerCase().replace(/\s/g, '-')}`;
    const hasError = Boolean(error);

    const containerClasses = [styles.container, fullWidth ? styles.fullWidth : '', className]
      .filter(Boolean)
      .join(' ');

    const inputWrapperClasses = [
      styles.inputWrapper,
      hasError ? styles.inputError : '',
      startIcon ? styles.hasStartIcon : '',
      endIcon ? styles.hasEndIcon : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <div className={inputWrapperClasses}>
          {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
          <input ref={ref} id={inputId} className={styles.input} aria-invalid={hasError} {...props} />
          {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
        </div>
        {(helperText || error) && (
          <span className={hasError ? styles.errorText : styles.helperText}>
            {error || helperText}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

